import { formatGPSID, GPS_ID_PATTERN } from "./gps";

// Port of GPSAssignmentPlanner (Views/GPSAssignmentView.swift): assigning an
// in-use GPS ID swaps the two devices, and every device with a missing,
// invalid, or duplicate ID is backfilled with the lowest free number. The
// planner re-plans the whole fleet so the transaction writer can apply only
// the diffs.

export const GPS_IDS_EXHAUSTED_MESSAGE = "GPS IDs are exhausted for this competition.";
export const GPS_DEVICE_UNAVAILABLE_MESSAGE = "The selected GPS device is no longer available.";

export class GPSAssignmentPlanError extends Error {}

export interface GPSPlannerDocument {
  id: string;
  displayName: string;
  /** Raw stored gps-id; may be missing or invalid. */
  gpsID?: string;
}

export interface GPSAssignmentPlan {
  /** Planned 3-digit ID for every document, in input order. */
  assignments: ReadonlyMap<string, string>;
  /** Documents whose planned ID differs from the stored one (the writes). */
  changed: ReadonlyMap<string, string>;
  /** min(max(used numeric IDs) + 1, 1000); rules require 1..1000. */
  nextGPSID: number;
  /** The document that previously held desiredGPSID, when a swap happened. */
  conflict?: GPSPlannerDocument;
}

function isValidGPSID(value: string | undefined): value is string {
  return typeof value === "string" && GPS_ID_PATTERN.test(value);
}

export function planGPSAssignments(
  documents: GPSPlannerDocument[],
  targetDocumentID: string,
  desiredGPSID: string,
): GPSAssignmentPlan {
  const target = documents.find((document) => document.id === targetDocumentID);
  if (!target) throw new GPSAssignmentPlanError(GPS_DEVICE_UNAVAILABLE_MESSAGE);

  const conflict = documents.find(
    (document) => document.id !== targetDocumentID && document.gpsID === desiredGPSID,
  );

  const reserved = new Map<string, string>([[targetDocumentID, desiredGPSID]]);
  if (conflict && isValidGPSID(target.gpsID) && target.gpsID !== desiredGPSID) {
    reserved.set(conflict.id, target.gpsID);
  }

  const assignments = new Map<string, string>();
  const usedIDs = new Set<string>();
  let nextCandidate = 1;

  const takeLowestFree = (): string => {
    while (nextCandidate <= 999) {
      const candidate = formatGPSID(nextCandidate);
      nextCandidate += 1;
      if (!usedIDs.has(candidate)) return candidate;
    }
    throw new GPSAssignmentPlanError(GPS_IDS_EXHAUSTED_MESSAGE);
  };

  for (const document of documents) {
    const reservedID = reserved.get(document.id);
    let assignedID: string;
    if (reservedID && !usedIDs.has(reservedID)) {
      assignedID = reservedID;
    } else if (isValidGPSID(document.gpsID) && !usedIDs.has(document.gpsID)) {
      assignedID = document.gpsID;
    } else {
      assignedID = takeLowestFree();
    }
    usedIDs.add(assignedID);
    assignments.set(document.id, assignedID);
  }

  const changed = new Map<string, string>();
  for (const document of documents) {
    const assignedID = assignments.get(document.id)!;
    if (document.gpsID !== assignedID) changed.set(document.id, assignedID);
  }

  const highestUsed = Math.max(0, ...[...usedIDs].map((id) => Number(id)));
  const nextGPSID = Math.min(highestUsed + 1, 1000);

  return { assignments, changed, nextGPSID, conflict: conflict ?? undefined };
}

/** "7" -> "007"; null when empty, non-numeric, or outside 1..999. */
export function normalizeGPSIDInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || !/^[0-9]+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (value < 1 || value > 999) return null;
  return formatGPSID(value);
}

export type GPSIDValidation =
  | { kind: "empty"; message: "Enter a GPS ID." }
  | { kind: "nonNumeric"; message: "Use numbers only." }
  | { kind: "outOfRange"; message: "Use a value from 001 to 999." }
  | { kind: "noChange"; message: "No change" }
  | { kind: "swap"; message: string; normalized: string }
  | { kind: "ready"; message: "Ready"; normalized: string };

/** Mirrors GPSAssignmentModel.validationMessage(for:) — Apply is enabled only for ready/swap. */
export function validateGPSIDInput(
  raw: string,
  target: GPSPlannerDocument,
  documents: GPSPlannerDocument[],
): GPSIDValidation {
  const trimmed = raw.trim();
  if (!trimmed) return { kind: "empty", message: "Enter a GPS ID." };
  if (!/^[0-9]+$/.test(trimmed)) return { kind: "nonNumeric", message: "Use numbers only." };
  const value = Number(trimmed);
  if (value < 1 || value > 999) return { kind: "outOfRange", message: "Use a value from 001 to 999." };
  const normalized = formatGPSID(value);
  if (normalized === target.gpsID) return { kind: "noChange", message: "No change" };
  const holder = documents.find((document) => document.id !== target.id && document.gpsID === normalized);
  if (holder) return { kind: "swap", message: `Will swap with ${holder.displayName}`, normalized };
  return { kind: "ready", message: "Ready", normalized };
}

/** iOS list order: Int(gps-id) ascending, missing/invalid last, name tie-break. */
export function sortPlannerDocuments<T extends GPSPlannerDocument>(documents: T[]): T[] {
  return [...documents].sort((a, b) => {
    const aNumber = isValidGPSID(a.gpsID) ? Number(a.gpsID) : Number.MAX_SAFE_INTEGER;
    const bNumber = isValidGPSID(b.gpsID) ? Number(b.gpsID) : Number.MAX_SAFE_INTEGER;
    if (aNumber !== bNumber) return aNumber - bNumber;
    return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" });
  });
}
